import {
    Promotion,
    PromotionBonusType,
    PromotionStatus,
} from '@app/promotionHub/promotions/types/Promotion';
import {
    BonusKind,
    ComtradeBonus,
} from '@core/models/bonus/Bonus';
import BonusProvider from '@core/models/bonus/BonusProvider';
import { WelcomeBonus } from '@core/models/bonus/WelcomeBonus';
import { minDate } from '@superbet-group/web.lib.utilities';
import { orderBy as _orderBy } from 'lodash';

const welcomeBonusCodes = [
    'CasinoWelcome',
    'NewWelcomeBonus',
    'WelcomeBonusV3',
    'WelcomeBonusV4',
    'NewCasinoWelcome',
    'SBWelcome',
    'NoBonus',
    'WelcomeFreeBetBonus',
];
const dummyWOPromotion: Promotion = {
    promotionId: 1,
    title: 'promotion.card.welcome_offer_title',
    description: 'promotion.card.welcome_offer_description',
    type: 1,
    status: PromotionStatus.Available,
    image: '/static/img/promotions/welcome-offer.png',
    conditions: [],
    expirationDate: new Date(),
    optInCode: 'WelcomeDummy',
    bonusSummaryTitle: 'promotion.card.welcome_offer.choose_your_bonus',
    bonus: {
        name: 'WelcomeOffer',
        type: PromotionBonusType.WelcomeOffer,
    },
};

const dummyComtradeWOPromotion: Promotion = {
    kind: BonusKind.Comtrade,
    ...dummyWOPromotion,
};

const dummyBetlerWOPromotion: Promotion = {
    kind: BonusKind.Betler,
    welcomeBonuses: [],
    isMultiConditionPromotion: false,
    ...dummyWOPromotion,
};

export function adjustWelcomeBonusPromotions(promotions: Promotion[], bonusProvider: BonusProvider, welcomeBonuses?: WelcomeBonus[]): Promotion[] {
    if (bonusProvider === BonusProvider.Comtrade) {
        promotions.unshift(dummyComtradeWOPromotion);
    } else if (welcomeBonuses?.length) {
        dummyBetlerWOPromotion.welcomeBonuses = welcomeBonuses;
        promotions.unshift(dummyBetlerWOPromotion);
    }

    return promotions;
}

export function sortPromotions(promotions: Promotion[]): Promotion[] {
    return _orderBy(
        promotions,
        [
            (promotion) => minDate([
                promotion.expirationDate ?? new Date(8.64e15),
                promotion.bonus.expirationDate ?? new Date(8.64e15),
            ]),
        ],
        [
            'asc',
        ],
    );
}

export function sortInProgressPromotions(promotion: Promotion[]): Promotion[] {
    return _orderBy(promotion, (promotion) => promotion.optInDate, 'asc');
}

export function sortAndGroupPromotions(promotions: Promotion[]) {
    const sortedPromotions = sortPromotions(promotions);
    const availablePromotions = getPromotionsByStatus(sortedPromotions, PromotionStatus.Available);
    const inProgressPromotions = getPromotionsByStatus(sortedPromotions, PromotionStatus.InProgress);
    const historyPromotions = sortedPromotions.filter((promotion) => promotion.status === PromotionStatus.History || promotion.status === PromotionStatus.Completed);
    const expiredPromotions = getPromotionsByStatus(sortedPromotions, PromotionStatus.Expired);

    return {
        availablePromotions,
        inProgressPromotions: sortInProgressPromotions(inProgressPromotions),
        historyPromotions,
        expiredPromotions,
    };
}

export function isPromotionWelcomeOffer(promotionOptInCode: string | null | undefined): boolean {
    return !!promotionOptInCode && welcomeBonusCodes.includes(promotionOptInCode);
}

export function fillPromotionsWithBonusData(promotions: Promotion[], bonuses: ComtradeBonus[]): Promotion[] {
    return promotions.map((promotion: Promotion) => {
        const promotionBonus = bonuses.filter((bonus: ComtradeBonus) => bonus.promotionId === promotion.promotionId);
        if (promotionBonus) {
            return {
                ...promotion,
                status: getUpdatedPromotionStatus(promotionBonus, promotion.status),
                bonus: {
                    ...promotion.bonus,
                    priority: promotionBonus[0]?.priority,
                    acceptedDate: promotionBonus[0]?.acceptanceDate,
                },
            };
        } else {
            return promotion;
        }
    });
}

function getPromotionsByStatus(promotions: Promotion[], status: PromotionStatus): Promotion[] {
    return promotions.filter((promotion) => promotion.status === status);
}

/**
 * Checks if all awarding conditions are met and updates promotion status accordingly.
 * If promotion status was InProgress and all awarding conditions are met, promotion status is updated to Completed.
 */
function getUpdatedPromotionStatus(promotionBonus: ComtradeBonus[], promotionStatus: PromotionStatus): PromotionStatus {
    const awardingConditions = promotionBonus.flatMap((bonus) => bonus.awardingConditions ? bonus.awardingConditions : []);
    if (awardingConditions.some((condition) => !condition.isConditionMet)) {
        return PromotionStatus.InProgress;
    } else if (promotionStatus === PromotionStatus.InProgress) {
        return PromotionStatus.Completed;
    } else {
        return promotionStatus;
    }
}
